import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Sparkles, Shield, Zap } from "lucide-react";
import { ToolCard } from "@/components/ToolCard";
import { useTools } from "@/lib/useTools";
import { catStyle, catDot } from "@/lib/api";

const HERO_TILT_TOOLS = ["merge-pdf", "compress-pdf", "pdf-to-word", "resize-image", "excel-to-pdf", "ai-summarize"];

export default function Home() {
  const { tools, categories } = useTools();
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let list = tools;
    if (activeCat !== "all") list = list.filter((t) => t.category === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
    }
    return list;
  }, [tools, activeCat, query]);

  const total = tools.length;

  return (
    <div className="pt-16">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60 grain">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-4xl">
            <div className="label-mono mb-6 inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              {total}+ Tools · One Platform
            </div>
            <h1 className="font-cabinet text-5xl font-bold tracking-tighter text-foreground sm:text-6xl lg:text-7xl">
              Every document tool. <span className="text-brand">One place.</span>
            </h1>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <a href="#tools" data-testid="hero-cta-browse" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 font-medium text-white transition-colors hover:bg-brand-hover">
                Browse all tools <ArrowRight className="h-4 w-4" />
              </a>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-emerald-500" /> Secure</div>
                <div className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-500" /> Fast</div>
                <div className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-brand" /> AI-powered</div>
              </div>
            </div>
          </div>

          {/* Category stat pills */}
          <div className="mt-16 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="category-stats">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2.5">
                <span className={`h-2 w-2 rounded-full ${catDot(c.id)}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{c.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{c.count} tools</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS GRID */}
      <section id="tools" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="label-mono mb-2">Explore</div>
            <h2 className="font-cabinet text-3xl font-semibold tracking-tight sm:text-4xl">All tools</h2>
          </div>
          <input
            data-testid="tools-search-input"
            placeholder="Filter tools…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-full border border-border bg-secondary/40 px-4 py-2 text-sm outline-none transition-colors focus:border-brand focus:bg-background md:w-72"
          />
        </div>

        {/* Category tabs */}
        <div className="mb-8 -mx-4 overflow-x-auto px-4">
          <div className="flex gap-2 whitespace-nowrap pb-2">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="category-tab-all"
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${activeCat === "all" ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              All · {total}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`category-tab-${c.id}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${activeCat === c.id ? `ring-1 ${catStyle(c.id)} border-transparent` : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${catDot(c.id)}`} />
                {c.name} · {c.count}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="tools-grid">
          {filtered.map((t) => <ToolCard key={t.id} tool={t} />)}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-16 text-center">
            <div className="label-mono mb-2">No results</div>
            <p className="text-muted-foreground">Try a different search or category.</p>
          </div>
        )}
      </section>
    </div>
  );
}
